import { Link } from "@tanstack/react-router";
import { ChevronSmallRightIcon } from "@wags/ui/product-icons.tsx";
import { RatingStars } from "@/components/shared/ReviewCard";
import { adoptionLabel, type Review } from "@/components/shared/ReviewsSection";

function plainText(text: string) {
    return text
        .replace(/(\*\*|__|\*|`|~~)(.+?)\1/g, "$2")
        .replace(/^#{1,6}\s+/gm, "")
        .replace(/\s+/g, " ")
        .trim();
}

function seeAllLabel(count: number) {
    if (count === 1) return "Read the review";
    return `See all ${count.toLocaleString()} reviews`;
}

function byline(review: Review) {
    const author = review.author === "Anonymous" ? null : review.author;
    return [author, adoptionLabel(review)].filter(Boolean).join(" · ");
}

export function ListingReviewsPeek({
    reviewCount,
    reviews,
    organizationId,
}: {
    reviewCount: number;
    reviews: Review[];
    organizationId: string;
}) {
    const review = reviews.find((candidate) => plainText(candidate.text));
    if (reviews.length === 0) return null;
    const caption = review ? byline(review) : "";

    return (
        <figure className="mt-5 px-1">
            {review ? (
                <>
                    <span
                        aria-hidden="true"
                        className="block h-6 font-family-heading text-5xl leading-none text-doberman-black/20"
                    >
                        &ldquo;
                    </span>
                    <blockquote className="line-clamp-4 break-words text-body text-doberman-black/85 text-pretty">
                        {plainText(review.text)}
                    </blockquote>
                </>
            ) : null}
            <figcaption className="mt-3 flex flex-col items-start gap-2">
                {caption ? (
                    <span className="min-w-0 truncate text-caption text-doberman-black/55">
                        {caption}
                    </span>
                ) : null}
                {review?.rating != null && (
                    <RatingStars rating={review.rating} />
                )}
                <Link
                    to="/provider/$providerId/reviews"
                    params={{ providerId: organizationId }}
                    className="relative -my-1 flex shrink-0 items-center gap-0.5 rounded-sm py-1 text-body-small font-medium text-doberman-black/60 transition-colors duration-150 after:absolute after:-inset-x-2 after:-inset-y-2.5 [@media(hover:hover)]:hover:text-doberman-black motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-labrador-yellow"
                >
                    {seeAllLabel(reviewCount)}
                    <ChevronSmallRightIcon
                        aria-hidden="true"
                        className="size-4"
                    />
                </Link>
            </figcaption>
        </figure>
    );
}
